import { PlanetClassType } from '@apeiron/planet'
import { AnyObject } from '@apeiron/temp'
import { GUILD_WAR_LEADERBOARD_NO_VALUE } from '@src/constants/battleDemo'
import {
  BattleDemoGuildPvPLeaderboard,
  BattleDemoGuildPvPRank,
  BattleDemoSoloPvELeaderboard,
  BattleDemoSoloPvERank,
  BattleDemoSoloPvPLeaderboard,
  BattleDemoSoloPvPRank
} from '@src/types/battleDemo'
import * as R from 'ramda'
import { useCallback } from 'react'

const useMakeLeaderboard = (): Hook => {
  const makeSoloPvERank = useCallback(
    (result: AnyObject): BattleDemoSoloPvERank => {
      const rank = Number(R.propOr(0, 'rank', result))

      return {
        id: rank.toString(),
        rank,
        planet: {
          icon: R.pathOr('', ['planet', 'classIcon'], result),
          id: R.pathOr('', ['planet', 'id'], result),
          planetClass: {
            image: R.pathOr('', ['planet', 'metaKey', 'image_path'], result),
            name: R.pathOr('', ['planet', 'metaKey', 'name'], result),
            type: PlanetClassType.Fate
          }
        },
        user: {
          name: R.pathOr('', ['user', 'name'], result),
          tag: Number(R.pathOr('', ['user', 'tag'], result)),
          walletAddress: R.pathOr('', ['user', 'walletAddress'], result)
        },
        score: Number(R.propOr(0, 'score', result))
      }
    },
    []
  )

  const makeSoloPvPRank = useCallback(
    (result: AnyObject): BattleDemoSoloPvPRank => {
      // support rank / rankingIndex
      const rank = Number(R.propOr(0, 'rank', result))

      return {
        id: rank.toString(),
        matchMakingRate: Number(R.propOr(0, 'mmr', result)),
        rank,
        totalStar: Number(R.propOr(0, 'stars', result)),
        user: {
          name: R.propOr('', 'userName', result),
          tag: Number(R.propOr('', 'userTag', result)),
          walletAddress: R.propOr('', 'walletAddress', result)
        }
      }
    },
    []
  )

  const makeGuildPvPRank = useCallback(
    (result: AnyObject): BattleDemoGuildPvPRank => {
      let rank = GUILD_WAR_LEADERBOARD_NO_VALUE

      if (R.has('rank', result)) {
        rank = Number(R.prop('rank', result))
      } else if (R.has('rankingIndex', result)) {
        rank = Number(R.prop('rankingIndex', result)) + 1
      }

      return {
        id: rank.toString(),
        matchMakingRate: Number(R.pathOr(0, ['guildTotal', 'mmr'], result)),
        rank,
        totalStar: Number(R.pathOr(0, ['guildTotal', 'stars'], result)),
        guild: {
          icon: R.pathOr('', ['guild', 'iconURL'], result),
          id: R.pathOr('', ['guild', 'id'], result),
          name: R.pathOr('', ['guild', 'name'], result),
          tag: Number(R.pathOr('', ['guild', 'tag'], result))
        }
      }
    },
    []
  )

  const makeSoloPvELeaderboard = useCallback(
    (
      first: number,
      offset: number,
      result: AnyObject
    ): BattleDemoSoloPvELeaderboard => {
      const total = R.pathOr(
        0,
        ['recordInfo', 'pageInfo', 'totalCount'],
        result
      )

      const page = offset / first

      const pageSize = first

      const playerRank = R.pathOr(null, ['recordInfo', 'playerRecord'], result)

      return {
        summary: {
          end: R.pathOr('', ['period', 'end'], result),
          start: R.pathOr('', ['period', 'start'], result)
        },
        record: {
          pageInfo: {
            hasNextPage: R.pathOr(false, ['recordInfo', 'hasNextPage'], result),
            hasPreviousPage: R.pathOr(
              false,
              ['recordInfo', 'hasPreviousPage'],
              result
            ),
            total,
            totalPage: total / pageSize + (total % pageSize === 0 ? 0 : 1),
            page,
            pageSize
          },
          playerRank: R.isNotNil(playerRank)
            ? makeSoloPvERank(playerRank)
            : null,
          ranks: R.map(
            makeSoloPvERank,
            R.pathOr([], ['recordInfo', 'globalRecords'], result)
          )
        }
      }
    },
    [makeSoloPvERank]
  )

  const makeSoloPvPLeaderboard = useCallback(
    (result: AnyObject): BattleDemoSoloPvPLeaderboard => {
      const playerRank = R.pathOr(null, ['record', 'userGuildRank'], result)

      return {
        playerRank: R.isNotNil(playerRank) ? makeSoloPvPRank(playerRank) : null,
        ranks: R.map(makeSoloPvPRank, R.propOr([], 'records', result))
      }
    },
    [makeSoloPvPRank]
  )

  const makeGuildPvPLeaderboard = useCallback(
    (result: AnyObject): BattleDemoGuildPvPLeaderboard => {
      const records = R.pathOr([], ['record', 'leaderboard'], result)

      const playerGuildId = R.pathOr(
        null,
        ['record', 'userGuildRank', 'guild', 'id'],
        result
      )

      const playerRank = R.isNotNil(playerGuildId)
        ? R.find(
            record => R.pathEq(playerGuildId, ['guild', 'id'], record),
            records
          )
        : null

      return {
        playerRank: R.isNotNil(playerRank)
          ? makeGuildPvPRank(playerRank)
          : null,
        ranks: R.map(makeGuildPvPRank, records)
      }
    },
    [makeGuildPvPRank]
  )

  return {
    makeSoloPvELeaderboard,
    makeSoloPvPLeaderboard,
    makeGuildPvPLeaderboard
  }
}

type Hook = {
  makeSoloPvELeaderboard: (
    first: number,
    offset: number,
    result: AnyObject
  ) => BattleDemoSoloPvELeaderboard
  makeSoloPvPLeaderboard: (result: AnyObject) => BattleDemoSoloPvPLeaderboard
  makeGuildPvPLeaderboard: (result: AnyObject) => BattleDemoGuildPvPLeaderboard
}

export default useMakeLeaderboard
